import type { DropdownItem } from 'src/types/webContracts';
import type {QVueGlobals} from 'quasar';
import { getCanaryActivationNextStep } from 'src/api/endpoints/patientWeb';
import { handleApiRequest } from 'src/api/handleApiRequest';
import {useDashboardStore} from 'src/store';

export async function getNextActivationRoute($q: QVueGlobals) {
  $q.loading.show()

  const dashboardStore = useDashboardStore();

  const nextStepResponse = await handleApiRequest(() => getCanaryActivationNextStep(), null, $q).finally(() => {
    $q.loading.hide()
  });

  // in case of error, we want to stay on the same page
  const hasError = nextStepResponse == undefined || nextStepResponse?.errorMessage?.length > 0 || nextStepResponse?.result?.message?.length > 0;
  if (hasError) {
    return;
  }

  const nextStep = nextStepResponse?.result?.nextStep;
  if(!dashboardStore.isLoading && dashboardStore.patient != null){
    dashboardStore.patient.personaIQSetupStatus =  nextStepResponse?.result?.completedSegment;
  }

  const page = Object.entries(PIQSetupPages).find(([key, value]) => value === nextStep);

  // if next page is not found, use Home page
  const uri = !!page && page.length > 1 ? `PIQSetup.${page[1]}` : 'Dashboard.Home'

  return uri;
}

export async function getPreviousActivationRoute(currentPage: PIQSetupPages) {
  let currentStep = getStepIndexByValues(currentPage);
  return getActivationRoute(--currentStep);
}

// Return the routeName of the page
export async function getActivationRoute(step: number) {
  let page = Object.values(PIQSetupPages)[step - 1];

  // If step doesn't exist default to first step.
  if (!page || page == PIQSetupPages.Terms)
    page = Object.values(PIQSetupPages)[0];
  return `PIQSetup.${page}`;
}

export function getStepIndexByValues(value: PIQSetupPages): number {
  const indexOfS = Object.values(PIQSetupPages).indexOf(value);
  return indexOfS + 1;
}

export function generateTimeOptions(): DropdownItem[] {
  const result: any[] = [];
  for (let x = 0; x < 24; x++) {
    let suffix = 'AM';
    let hourNumber = x % 12;
    if (hourNumber === 0) {
      hourNumber = 12;
    }

    if (x > 11) {
      suffix = 'PM';
    }

    const combinedValue = `${hourNumber}:00 ${suffix}`;
    const newOption = {
      value: x,
      text: combinedValue,
      '24H': (x == 0)? '00:00': (x < 10 )? `0${x}:00`:`${x}:00`
    };
    result.push(newOption);
  }
  return result;
}

// These values match response from api endpoint v2/1/GetNextCanaryActivationStep
export enum PIQSetupPages {
  // Segment 1
  Start = 'Start',       // API returns 'CanaryTermsAndConditions' for this page
  Terms = 'CanaryTerms',
  PrivacyPolicy = 'CanaryPrivacy',
  Address = 'CanaryMailingAddress',
  Caregiver = 'CanaryCaregiver',
  HeightWeight = 'CanaryHeightWeight',
  // Segment 2
  CanaryHasHBS = 'CanaryHasHBS',
  CanaryHBSVersion = 'CanaryHBSVersion',  // API doesn't return a value for this page
  CanaryBedtime = 'CanaryBedtime',
  CanaryHBSComputerRequirements = 'CanaryHBSComputerRequirements',
  // Segment 3
  CanaryAlmostDone = 'CanaryAlmostDone',

  // After segment 3
  Complete = 'CanaryActivationComplete'
}
